import React from 'react';
import Link from 'next/link';
import styled from 'styled-components';

import Imgix from 'shared/components/imgix';
import { getCategoryPhoto } from 'shared/helpers/dispensaries';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { DispensaryMenuCategory } from 'src/dispensary/hooks/use-dispensary-categories';
import { CategoriesTypeOption, RadiusOption } from 'src/gql/graphql';

type SpecialsCategoryCardProps = {
  category: DispensaryMenuCategory;
  href: string;
  type: CategoriesTypeOption;
  style: RadiusOption;
};

// Matches the radius scale used by the main category nav's image cards (large/compact/stacked)
const imageCardRadiusOptions: Record<RadiusOption, number | string> = {
  [RadiusOption.rounded]: '16px',
  [RadiusOption.roundedRectangle]: '12px',
  [RadiusOption.square]: 0,
};

// Matches the radius scale used by the main category nav's text pills
const textPillRadiusOptions: Record<RadiusOption, number | string> = {
  [RadiusOption.rounded]: '9999px',
  [RadiusOption.roundedRectangle]: '12px',
  [RadiusOption.square]: 0,
};

export function SpecialsCategoryCard({ category, href, type, style }: SpecialsCategoryCardProps): JSX.Element {
  const { dispensary } = useDispensary();
  const { label, cardImage } = category;

  if (type === CategoriesTypeOption.text) {
    return (
      <TextPillLink href={href} $radius={textPillRadiusOptions[style]} data-testid='specials-category-card'>
        {label}
      </TextPillLink>
    );
  }

  const image = cardImage ?? getCategoryPhoto(dispensary, category);

  return (
    <CardLink href={href} $radius={imageCardRadiusOptions[style]} data-testid='specials-category-card'>
      <Label>{label}</Label>
      <ImageContainer>
        <StyledImage
          src={image}
          disableSrcSet
          imgixParams={{ height: 224 }}
          htmlAttributes={{ alt: `${label} category image` }}
        />
      </ImageContainer>
    </CardLink>
  );
}

const CardLink = styled(Link)<{ $radius: number | string }>`
  background: ${({ theme }) => theme.colors?.white ?? 'white'};
  border: 1px solid ${({ theme }) => theme.colors?.greyBorder ?? '#e3e7e9'};
  border-radius: ${({ $radius }) => $radius};
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px;
  text-decoration: none;
  cursor: pointer;
  overflow: hidden;
  width: 151px;
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  @media (min-width: 960px) {
    gap: 28px;
    padding: 20px;
    width: 184px;
  }
`;

const TextPillLink = styled(Link)<{ $radius: number | string }>`
  background: ${({ theme }) => theme.colors?.white ?? 'white'};
  border: 2px solid ${({ theme }) => theme.colors?.greyBorder ?? '#e3e7e9'};
  border-radius: ${({ $radius }) => $radius};
  color: ${({ theme }) => theme.colors?.primaryBlack ?? '#121516'};
  display: block;
  font-size: 16px;
  font-weight: 600;
  line-height: ${24 / 16};
  padding: 12px 20px;
  text-align: center;
  text-decoration: none;
  white-space: nowrap;
  transition: border 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors?.primaryBlack ?? '#121516'};
  }
`;

const ImageContainer = styled.div`
  aspect-ratio: 640 / 340;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledImage = styled(Imgix)`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Label = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors?.primaryBlack ?? '#121516'};
  line-height: 20px;
  width: 100%;

  @media (min-width: 960px) {
    font-size: 20px;
    line-height: 24px;
  }
`;
